import React, { useState, useEffect } from 'react';
import DragDropArea from './DragDropArea';
import AIGenerationInfo from './AIGenerationInfo';
import MetadataTree from './MetadataTree';
import RawJSONView from './RawJSONView';
import SearchBar from './SearchBar';
import FileSummary from './FileSummary';
import FrameQC from './FrameQC';
import CutScan from './CutScan';
import { extractMetadata } from '../utils/metadataExtractor';
import { analyzeBlankFrames, extractTiming } from '../utils/blackFrameDetector';
import useAnalytics from '../hooks/useAnalytics';
import { Bot, Search, BarChart3 } from 'lucide-react';

function HomePage() {
    const [file, setFile] = useState(null);
    const [metadata, setMetadata] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [previewUrl, setPreviewUrl] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [frameQC, setFrameQC] = useState(null);
    const [qcLoading, setQcLoading] = useState(false);
    const { trackEvent } = useAnalytics();

    useEffect(() => {
        if (file) {
            const url = URL.createObjectURL(file);
            setPreviewUrl(url);
            return () => URL.revokeObjectURL(url);
        } else {
            setPreviewUrl(null);
        }
    }, [file]);

    const handleFileUpload = async (uploadedFile) => {
        setError(null);
        setIsLoading(true);
        setFile(uploadedFile);
        setFrameQC(null);
        let extractedMetadata = null;
        try {
            extractedMetadata = await extractMetadata(uploadedFile);
            setMetadata(extractedMetadata);
            trackEvent('extract_metadata', {
                file_type: uploadedFile.type,
                is_ai_generated: extractedMetadata.isAIGenerated,
                ai_generator: extractedMetadata.aiGenerator || 'none'
            });
        } catch (err) {
            setError('Error processing file: ' + err.message);
            setMetadata(null);
            trackEvent('extraction_error', {
                error_message: err.message,
                file_type: uploadedFile.type
            });
        } finally {
            setIsLoading(false);
        }

        // Blank/black-frame check (videos only), after metadata renders.
        if (extractedMetadata && uploadedFile.type.startsWith('video/')) {
            setQcLoading(true);
            try {
                const qc = await analyzeBlankFrames(uploadedFile, extractTiming(extractedMetadata));
                setFrameQC(qc);
            } catch (e) {
                console.error('Frame QC failed:', e);
                setFrameQC(null);
            } finally {
                setQcLoading(false);
            }
        }
    };

    return (
        <>
            {!file && !isLoading && (
                <div className="welcome-section">
                    <DragDropArea onFileSelect={handleFileUpload} />
                    <div className="features">
                        <div className="feature">
                            <Bot size={16} className="feature-icon" />
                            <span className="feature-text">Detect AI-generated content</span>
                        </div>
                        <div className="feature">
                            <Search size={16} className="feature-icon" />
                            <span className="feature-text">Extract hidden prompts</span>
                        </div>
                        <div className="feature">
                            <BarChart3 size={16} className="feature-icon" />
                            <span className="feature-text">View complete metadata</span>
                        </div>
                    </div>
                </div>
            )}

            {file && !isLoading && (
                <div className="top-bar">
                    <div className="file-info">
                        <span className="file-name">{file.name}</span>
                        <span className="file-size">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                    </div>
                    <SearchBar onSearch={setSearchTerm} />
                </div>
            )}

            {isLoading && (
                <div className="loading">
                    <div className="spinner"></div>
                    <p className="loading-text">Analyzing file...</p>
                    <p className="loading-subtext">
                        {file?.type.startsWith('video/') ? 'Scanning video file...' : 'Scanning image file...'}
                    </p>
                </div>
            )}

            {file && !isLoading && (
                <div className="metadata-container">
                    <div className="card preview-card">
                        <DragDropArea onFileSelect={handleFileUpload} isCompact={true} previewUrl={previewUrl} fileType={file?.type} fileName={file?.name} />
                        <FileSummary metadata={metadata} file={file} />
                        <FrameQC result={frameQC} loading={qcLoading} />
                        <CutScan file={file} />
                    </div>
                    <div className="card">
                        <AIGenerationInfo metadata={metadata} fileType={file.type} />
                    </div>
                    <div className="card">
                        <MetadataTree metadata={metadata} searchTerm={searchTerm} aiDetection={metadata} />
                    </div>
                    <div className="card">
                        <RawJSONView metadata={metadata} searchTerm={searchTerm} />
                    </div>
                </div>
            )}

            {error && (
                <div className="error">
                    <span className="error-icon">⚠️</span>
                    <span>{error}</span>
                </div>
            )}
        </>
    );
}

export default HomePage;
